
import React from "react";
import { Input } from "@/components/ui/input";
import { useForm } from "@/context/FormContext";
import { FormFieldType } from "@/types/form";
import FieldWrapper from "./FieldWrapper";
import { AlertCircle, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface FileFieldProps {
  element: FormFieldType;
}

export const FileField: React.FC<FileFieldProps> = ({ element }) => {
  const { formData, setField, errors } = useForm();
  const maxFiles = element.validation?.maxFiles || 1;
  const maxSizeMB = element.validation?.maxSizeMB || 10;
  const [error, setError] = React.useState<string | null>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setError(null);
    
    if (!files || files.length === 0) {
      setField(element.name!, []);
      return;
    }
    
    // Check max files
    if (files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }
    
    // Convert to array and check sizes
    const fileList: File[] = Array.from(files);
    
    // Check for any files that exceed size limit
    const oversizedFiles = fileList.filter(file => file.size > maxSizeMB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the ${maxSizeMB}MB size limit`);
      return;
    }
    
    setField(element.name!, fileList);
  };

  const removeFile = (index: number) => {
    if (formData[element.name!] && Array.isArray(formData[element.name!])) {
      // Explicitly cast the formData value to the correct type
      const files = formData[element.name!] as File[] | string[];
      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      // Cast back to the type expected by setField
      setField(element.name!, updatedFiles as any);
    }
  };

  return (
    <FieldWrapper element={element}>
      <div className="space-y-2">
        <Input
          id={element.id}
          type="file"
          onChange={handleFileChange}
          className="w-full cursor-pointer"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          multiple={maxFiles > 1}
          required={element.required}
        />
        
        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {errors[element.name!] && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors[element.name!]}</AlertDescription>
          </Alert>
        )}
        
        {formData[element.name!] && Array.isArray(formData[element.name!]) && (formData[element.name!] as Array<any>).length > 0 && (
          <div className="mt-2">
            <p className="text-sm font-medium">Files selected ({(formData[element.name!] as Array<any>).length}):</p>
            <ul className="list-disc pl-5 text-sm">
              {(formData[element.name!] as Array<any>).map((file, index) => (
                <li key={index} className="flex items-center justify-between py-1">
                  <span>
                    {typeof file === 'string' 
                      ? file.split('/').pop() 
                      : file.name} ({typeof file === 'string' 
                        ? 'Uploaded' 
                        : (file.size / (1024 * 1024)).toFixed(2) + 'MB'})
                  </span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 ml-2 text-gray-500 hover:text-red-500"
                    onClick={() => removeFile(index)}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove file</span>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <p className="text-xs text-gray-500">
          Maximum {maxSizeMB} MB per file
        </p>
      </div>
    </FieldWrapper>
  );
};

export default FileField;
